/**
 * Such-Helfer mit Umlaut-Normalisierung und Tippfehler-Toleranz.
 *
 * Strategie:
 *   1. Normalisierung:
 *      - Umlaute werden vereinheitlicht (ä→ae, ö→oe, ü→ue, ß→ss).
 *      - Akzente entfernt (Müller, Mueller, Mu\u00fcller → mueller).
 *      - Lowercase, Whitespace gestrippt.
 *      → "Schaler" findet "Schäler", weil beide → "schaler/schäler→schaeler".
 *
 *   2. Substring-Match auf normalisiertem Heuhaufen — das deckt 90 % der Faelle ab.
 *
 *   3. Falls kein Substring-Treffer: Token-weise Levenshtein-Distanz <= 2
 *      (nur fuer Tokens >= 4 Zeichen, sonst zu viele False-Positives).
 *      → "Maier" findet "Mayer", "Schmid" findet "Schmidt".
 */

const UMLAUT_REPLACEMENTS = {
  'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss',
  'Ä': 'ae', 'Ö': 'oe', 'Ü': 'ue',
};

export const normalizeForSearch = (text) => {
  if (text == null) return '';
  let s = String(text).toLowerCase();
  // Umlaute zuerst, BEVOR die Normalisierung sie zerlegt
  s = s.replace(/[äöüßÄÖÜ]/g, (c) => UMLAUT_REPLACEMENTS[c] || c);
  // Restliche Akzente entfernen (é, è, à, ñ ...)
  s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return s.trim();
};

/**
 * Iterative Levenshtein-Distanz mit Cap.
 * Returnt sofort `cap+1` sobald die Distanz den Cap uebersteigt — schnell.
 */
const levenshteinCapped = (a, b, cap) => {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost,
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > cap) return cap + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
};

/**
 * Prüft, ob `query` im `text` enthalten ist (mit Tippfehler-Toleranz).
 *
 * - Beide Seiten werden via `normalizeForSearch` vorbehandelt.
 * - Erst Substring-Match. Bei Treffer sofort true.
 * - Falls nein: Token-weise Levenshtein <= maxDistance fuer Worte >= 4 Zeichen.
 */
export const fuzzyMatch = (text, query, { maxDistance = 2 } = {}) => {
  const normText = normalizeForSearch(text);
  const normQuery = normalizeForSearch(query);
  if (!normQuery) return true;
  if (normText.includes(normQuery)) return true;

  // Token-Tippfehler-Match nur bei >= 4 Zeichen (sonst zu lax)
  if (normQuery.length < 4) return false;
  const tokens = normText.split(/\s+/).filter(Boolean);
  for (const token of tokens) {
    if (token.length < 4) continue;
    if (levenshteinCapped(token, normQuery, maxDistance) <= maxDistance) {
      return true;
    }
    // Auch Sub-Tokens des Querys mit erlauben (Mehrwort-Suche)
  }
  // Mehrwort-Query: jedes Wort einzeln tolerieren
  const queryParts = normQuery.split(/\s+/).filter((p) => p.length >= 4);
  if (queryParts.length === 0) return false;
  return queryParts.every((qp) =>
    tokens.some((t) => t.length >= 4 && levenshteinCapped(t, qp, maxDistance) <= maxDistance)
  );
};
